import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import DataTable from 'material-table';
import { Paper } from '@material-ui/core';
import {
  LoadingOverlay,
  makeStyles,
  ThemeProvider,
} from '@knockrentals/knock-shared-web';
import { Toaster } from '@knockrentals/knock-react';

import { FilterProperty } from './Components/FilterBoxes';
import { getVirtualAgents, updateVirtualAgent } from '../VirtualAgentAPI';
import { editAiEmailEnabled } from '../aiEmailAPI';
import { useFeatureFlagContext } from '../../../Context/FeatureFlagContext';
import { virtualAgentTableOptions } from './table-options';
import { getAILeasingColumns } from './getAILeasingColumns';
import { AI_LEASING_FIELDS } from './constants';

const useStyles = makeStyles({
  filterAndEditContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '10px',
    marginTop: '24px',
  },
});

export const API_ERROR = 'Error updating virtual agent';

const VirtualAgentConfigSettings = ({
  companyId,
  properties,
  setProductsEnabled,
}) => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [virtualAgents, setVirtualAgents] = useState([]);
  const [fetchedVirtualAgents, setFetchedVirtualAgents] = useState(false);
  const [filteredVirtualAgents, setFilteredVirtualAgents] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState(
    properties.map(({ propertyId }) => propertyId),
  );
  const filters = {};

  useEffect(() => {
    const getVirtualAgentsData = async () => {
      const propertyIds = properties.map(({ propertyId }) => propertyId);
      const virtualAgentsResponse = await getVirtualAgents(companyId, 1, 1000);
      const virtualAgents = (virtualAgentsResponse.results || [])
        .filter((virtualAgent) =>
          propertyIds.includes(virtualAgent.property_id),
        )
        .map((virtualAgent) => {
          const property = properties.find(
            (property) => property.propertyId === virtualAgent.property_id,
          );
          return {
            ...virtualAgent,
            propertyName: property.propertyName,
            ai_email_enabled: property.aiEmailEnabled,
            leasingEnabled: property.leasingEnabled,
          };
        });

      virtualAgents.sort((a, b) =>
        a.propertyName.localeCompare(b.propertyName),
      );
      setVirtualAgents(virtualAgents);
      setFilteredVirtualAgents(virtualAgents);
    };

    if (!fetchedVirtualAgents && properties.length) {
      setIsLoading(true);
      getVirtualAgentsData()
        .catch(() => {
          Toaster.showToast(
            'Error fetching virtual agent data',
            2000,
            Toaster.ToastClasses.error,
          );
        })
        .finally(() => {
          setFetchedVirtualAgents(true);
          setIsLoading(false);
        });
    }
  }, [fetchedVirtualAgents, companyId, properties]);

  const applyFilters = (currentFilters, currentSelectedProperties) => {
    const filteredVirtualAgents = virtualAgents.filter((virtualAgent) => {
      const isRowValid = Object.keys(currentFilters).every((key) => {
        if (currentFilters[key] === null) {
          return true;
        } else if (Array.isArray(currentFilters[key])) {
          return currentFilters[key].includes(virtualAgent[key]);
        } else if (key === 'ai_email_enabled') {
          return (
            (currentFilters[key] && virtualAgent[key] === 'ON') ||
            (!currentFilters[key] && virtualAgent[key] !== 'ON')
          );
        } else {
          return virtualAgent[key] === currentFilters[key];
        }
      });

      const isSelected = currentSelectedProperties.length
        ? currentSelectedProperties.includes(virtualAgent.property_id)
        : true;

      return isRowValid && isSelected;
    });

    setFilteredVirtualAgents(filteredVirtualAgents);
  };

  const handlePropertySelect = (properties) => {
    applyFilters(filters, properties);
    setSelectedProperties(properties);
  };

  const saveAiEmail = async (rowData, value) => {
    const aiEmailValue = value ? 'ON' : 'SETUP';
    const payload = [
      {
        property_id: rowData.property_id,
        company_id: companyId,
        [AI_LEASING_FIELDS.IS_AI_EMAIL_ENABLED]: aiEmailValue,
      },
    ];

    try {
      await editAiEmailEnabled(payload);
      Toaster.showToast('Saved!', 2000, Toaster.ToastClasses.success);

      const property = properties.find(
        (property) => property.propertyId === rowData.property_id,
      );
      property.aiEmailEnabled = aiEmailValue;
      rowData[AI_LEASING_FIELDS.IS_AI_EMAIL_ENABLED] = aiEmailValue;

      const existingIdx = filteredVirtualAgents.findIndex(
        (va) => va.id === rowData.id,
      );
      setFetchedVirtualAgents([
        ...filteredVirtualAgents.slice(0, existingIdx),
        rowData,
        ...filteredVirtualAgents.slice(
          existingIdx + 1,
          filteredVirtualAgents.length,
        ),
      ]);
      setProductsEnabled((prev) =>
        prev.map((row) =>
          row.propertyId === rowData.property_id
            ? { ...row, aiEmailEnabled: aiEmailValue }
            : row,
        ),
      );
    } catch (err) {
      Toaster.showToast(
        'Error saving AI email setting',
        2000,
        Toaster.ToastClasses.error,
      );
    }
  };

  const saveVaData = async (rowData, field, value) => {
    const payload = {
      virtualAgentId: rowData.id,
      propertyId: rowData.property_id,
      field,
      value,
    };

    try {
      await updateVirtualAgent(payload);
      Toaster.showToast('Saved!', 2000, Toaster.ToastClasses.success);
      rowData[field] = value;

      const existingIdx = filteredVirtualAgents.findIndex(
        (va) => va.id === rowData.id,
      );
      setFetchedVirtualAgents([
        ...filteredVirtualAgents.slice(0, existingIdx),
        rowData,
        ...filteredVirtualAgents.slice(
          existingIdx + 1,
          filteredVirtualAgents.length,
        ),
      ]);
    } catch (err) {
      Toaster.showToast(API_ERROR, 2000, Toaster.ToastClasses.error);
    }
  };

  const { isAiNurtureEnabled } = useFeatureFlagContext();

  return (
    <ThemeProvider>
      <div className={classes.filterAndEditContainer}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <FilterProperty
            properties={properties.map((property) => ({
              id: property.propertyId,
              name: property.propertyName,
              selected: selectedProperties.includes(property.propertyId),
            }))}
            onPropertySelect={handlePropertySelect}
          />
        </div>
      </div>
      <LoadingOverlay
        open={isLoading}
        style={{ position: 'absolute', opacity: '0.5' }}
      />
      <DataTable
        className="scheduling-grid-data-table"
        columns={getAILeasingColumns({
          isAiNurtureEnabled,
          saveVaData,
          saveAiEmail,
        })}
        components={{
          Container: (props) => <Paper {...props} variant="outlined" />
        }}
        data={filteredVirtualAgents}
        options={virtualAgentTableOptions}
      />
    </ThemeProvider>
  );
};

VirtualAgentConfigSettings.propTypes = {
  companyId: PropTypes.number.isRequired,
  properties: PropTypes.arrayOf(
    PropTypes.shape({
      aiEmailEnabled: PropTypes.oneOf(['ON', 'OFF', 'SETUP']),
      leasingEnabled: PropTypes.bool,
      propertyId: PropTypes.number.isRequired,
      propertyName: PropTypes.string.isRequired,
      residentEnabled: PropTypes.bool,
      tableData: PropTypes.shape({
        id: PropTypes.number,
      }),
    }),
  ).isRequired,
  setProductsEnabled: PropTypes.func.isRequired,
};

export default VirtualAgentConfigSettings;
